import Box from "@mui/material/Box";
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { CustomStyleFormHelperText, CustomStyleSuffixText, CustomStyleTextArea, CustomStyleTextField } from "./Styles";
import CustomTextFieldApp from "./CustomTextField/CustomTextFieldApp";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * @Description Input field (text, number)
 * @param String id: Identifier for the input field (required)
 * @param Integer length: Maximum length for the value in the text field (optional)
 * @param String counterColor: Color value for the counter legend (optional)
 * @param String name: Name for the input field (optional)
 * @param Boolean emptyFields: Indicates if the field is empty (optional)
 * @param Boolean isValid: Show legend and input with error when isValid = false (optional)
 * @param String isValidText: Legend for input with error (optional)
 * @param String value: Value for show in input field (required)
 * @param Boolean disabled: Enable/disable writing in the input field (optional)
 * @param String trans: Text for the label (required)
 * @param String type: Defines the input type: text/number (required)
 * @param String placeholder: Defines the placeholder text for the input field (optional)
 * @param Object inputProps: Defines attributes to apply to the native input field (optional)
 * @param JSX.Element InputProps: Defines an element (InputAdornment) to add to the input field (optional)
 * @param String backgroundColor: Background color value for the input field (optional)
 * @param String fontColor: Font color value for the input field (optional)
 * @param String suffixText: Text to show as an InputAdornment (optional)
 * @param Boolean countDecimals: If the text includes decimals, count them in the counter (optional)
 * @param Boolean standard: If standard == true, only show the bottom border in the input field (optional)
 * @param Boolean required: Input field is required (optional)
 * @param Boolean multiline: Is a textarea (optional)
 * @param Integer rows: When multiline, show these default lines in the input field (optional)
 * @param Integer maxRows: When multiline, show this maximum number of lines in the input field (optional)
 * @param String height: Defines the height for the input field (optional)
 * @param Boolean showCounter: If showCounter == true, count characters in the value and show them under the input field (optional)
 * @param String helperText: Text for the legend, displayed under the input field (optional)
 * @param Event onFocus: onFocus trigger in the input field (optional)
 * @param Event onBlur: onBlur trigger in the input field (optional)
 * @param Event onChange: onChange trigger in the input field (optional)
 * @param Event onKeyDown: onKeyDown trigger in the input field (optional)
 * @param Boolean showHelperTextAlways: If true, displays the helperText immediately below the input field
 *                                     regardless of the field's value or validation state.
 *                                     This bypasses the default conditional logic that only shows
 *                                     helperText when the field is empty and emptyFields is active.
 *                                     Default: false
 * @returns {JSX.Element}
 */

export const CustomTextField = (props) => {
    let {
        id,
        length = 60,
        counterColor = '#707070',
        name,
        emptyFields,
        isValid = true,
        isValidText = 'format_invalid',
        value,
        disabled = false,
        trans,
        type = "text",
        placeholder = '',
        inputProps = {},
        InputProps = false,
        backgroundColor = "transparent",
        fontColor = "",
        suffixText = '',
        countDecimals = false,
        standard = false,
        required = false,
        multiline = false,
        rows = 2,
        maxRows = 4,
        height = '',
        showCounter = true,
        helperText = '',
        showHelperTextAlways=false,
        onFocus,
        onBlur,
        onChange,
        onKeyDown,
        autoComplete = 'new-password',
        readOnly,
        click=null,
        marginBottom=true,
    } = props;

    const {
        t,
        style,
        customInputProps,
        lCode,
        valueEmpty,
        handleKeyDown,
        handleNumberInputChange,
        handlePaste,
        CharacterCount
    } = CustomTextFieldApp({
        type,
        length,
        height,
        backgroundColor,
        fontColor,
        inputProps,
        countDecimals,
        onKeyDown,
        onChange,
        value
    });

    return (
        <TextField size="small"
            sx={(multiline || length === "MAX") ? CustomStyleTextArea(lCode) : CustomStyleTextField(lCode, false)}
            name={name}
            type={type}
            fullWidth
            label={standard ? '' : t(trans)}
            value={value === 0 ? 0 : (value || '')}
            disabled={disabled}
            readOnly={readOnly}
            id={id}
            onWheelCapture={e => {
                e.target.blur()
            }}
            className={((emptyFields && valueEmpty) || (!isValid)) ? "input-required" : ""}
            placeholder={placeholder}
            onClick={(e)=>{
                if(click)
                    click(e);
            }}
             onChange={type === "number" ? handleNumberInputChange : onChange}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            onPaste={handlePaste}
            inputProps={{
                ...customInputProps,
                autoComplete: autoComplete,
                style: style
            }}
            InputProps={InputProps || {
                endAdornment: suffixText && (
                    <InputAdornment position="end">
                        <Box sx={CustomStyleSuffixText(disabled)}>
                            {suffixText}
                        </Box>
                    </InputAdornment>
                )
            }}
            multiline={multiline}
            minRows={multiline ? rows : undefined}
            maxRows={multiline ? maxRows : undefined}
            required={required}
            error={(emptyFields && valueEmpty) || (!isValid)}
                   helperText={
                       showHelperTextAlways && helperText !== '' ? (
                               <Box
                                   className={`flex-end ${!isValid ? 'Mui-error' : ''}`}
                                   sx={CustomStyleFormHelperText}
                                   disabled={disabled}
                               >
                                   {t(helperText)}
                               </Box>
                           ) :
                           !emptyFields && helperText !== '' && valueEmpty ? (
                                   <Box
                                       className={((emptyFields && valueEmpty) || (!isValid)) ? 'Mui-error' : ''}
                                       sx={CustomStyleFormHelperText}
                                       disabled={disabled}
                                   >
                                       {t(helperText)}
                                   </Box>
                               ) :
                               (
                                   <CharacterCount
                                       disabled={disabled}
                                       count={value?.toString()?.length || 0}
                                       counterColor={counterColor}
                                       helperText={emptyFields && valueEmpty ? t('requiredField') : !isValid ? t(isValidText) : ''}
                                       max={length}
                                       multiline={multiline}
                                       showCounter={showCounter}
                                       errorClass={((emptyFields && valueEmpty) || (!isValid)) ? 'Mui-error' : ''}
                                       marginBottom={marginBottom}
                                   />
                               )
                   }
        />
    );
};


